import { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from "axios";

import {
  buildRoutePath,
  type RouteBody,
  type RouteMeta,
  type RouteParams,
  type RouteQuery,
  type RouteResponse,
} from "@fitness-app/data-models/http/cloudFunctions";

/**
 * `params` wypełnia `:placeholdery` w ścieżce, `query` idzie do query stringa.
 * Dwa osobne pola, bo axios trzyma oba pod jednym `params` i bez rozdzielenia
 * parametr ścieżki lądował w URL-u dwa razy.
 */
type CallOptions<TRoute> = Omit<AxiosRequestConfig, "params"> &
  (RouteParams<TRoute> extends void ? { params?: undefined } : { params: RouteParams<TRoute> }) &
  (RouteQuery<TRoute> extends void ? { query?: undefined } : { query?: RouteQuery<TRoute> });

/**
 * Calls a route from the registry instead of a hand-written string
 * (TRE-1684):
 *
 * ```ts
 * await callFn(productApi, CloudFunctions.products.routes.addProduct, payload);
 * ```
 *
 * The path, the body type and the response type all come from the declaration,
 * so renaming a route or changing a body shape fails `pnpm type-check` here
 * instead of at runtime in front of a user.
 */
export async function callFn<TRoute extends RouteMeta>(
  api: AxiosInstance,
  route: TRoute,
  ...args: RouteBody<TRoute> extends void
    ? [body?: undefined, options?: CallOptions<TRoute>]
    : [body: RouteBody<TRoute>, options?: CallOptions<TRoute>]
): Promise<AxiosResponse<RouteResponse<TRoute>>> {
  const [body, options] = args;
  const { params, query, ...rest } = (options ?? {}) as AxiosRequestConfig & {
    params?: Record<string, string | number>;
    query?: Record<string, unknown>;
  };
  const path = buildRoutePath(route, params);
  const config: AxiosRequestConfig = query ? { ...rest, params: query } : rest;

  if (route.method === "get" || route.method === "delete") {
    return api[route.method]<RouteResponse<TRoute>>(path, config);
  }

  return api[route.method]<RouteResponse<TRoute>>(path, body, config);
}
